import {
	type CSSProperties,
	type ReactNode,
	type TransitionEvent,
	useLayoutEffect,
	useRef,
	useState,
} from "react";

import { cx } from "@/utils/cx";

export type AccordionProps =
	| ControlledAccordionProps
	| UncontrolledAccordionProps;

type AccordionItem = {
	content: ReactNode;
	defaultOpen?: boolean;
	/** Applied to the item header row (title + toggle). */
	headerClassName?: string;
	/** Stable identity for React keys (prefer over array index). */
	id?: string;
	title: ReactNode | string;
};

type AccordionTransition = {
	duration?: number;
	ease?: string;
	type?: "spring" | "tween";
};

const DEFAULT_ACCORDION_TRANSITION: AccordionTransition = {
	duration: 0.4,
	ease: "easeOut",
	type: "tween",
};

const indicesFromKey = (key: string): number[] =>
	key.length === 0 ? [] : key.split(",").map((part) => Number(part));

const resolveAccordionTransition = (
	transition: AccordionTransition,
): Pick<CSSProperties, "transitionDuration" | "transitionTimingFunction"> => {
	const duration = transition.duration ?? DEFAULT_ACCORDION_TRANSITION.duration;
	const ease = transition.ease ?? DEFAULT_ACCORDION_TRANSITION.ease;

	return {
		transitionDuration: `${duration}s`,
		transitionTimingFunction:
			ease === "easeOut" || ease === "ease-out"
				? "cubic-bezier(0.16, 1, 0.3, 1)"
				: ease === "easeIn" || ease === "ease-in"
					? "ease-in"
					: ease === "linear"
						? "linear"
						: "ease",
	};
};

type AccordionPropsBase = {
	buttonClassName?: string;
	className?: string;
	items: AccordionItem[];
	/**
	 * After a panel finishes opening, scroll its header to the top of the
	 * nearest overflow container (clamped when there isn't enough room below).
	 */
	scrollOpenedPanelIntoView?: boolean;
	/** Overrides the default spring used for panel expand/collapse. */
	transition?: AccordionTransition;
};

type ControlledAccordionProps = AccordionPropsBase & {
	expandAllItemsByDefault?: undefined;
	onOpenPanelsChange?: (openPanels: Set<number>) => void;
	openPanels: Set<number>;
};

type UncontrolledAccordionProps = AccordionPropsBase & {
	expandAllItemsByDefault?: boolean;
	onOpenPanelsChange?: undefined;
	openPanels?: undefined;
};

const isOverflowYScroller = (element: HTMLElement) => {
	const { overflowY } = getComputedStyle(element);
	return (
		overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay"
	);
};

const ACCORDION_SCROLL_CONTAINER = "accordionScrollContainer";

const getNearestOverflowYScroller = (node: HTMLElement): HTMLElement | null => {
	let current = node.parentElement;
	let fallback: HTMLElement | null = null;

	while (current) {
		if (current.dataset[ACCORDION_SCROLL_CONTAINER] === "true") {
			return current;
		}
		if (
			!fallback &&
			isOverflowYScroller(current) &&
			current.scrollHeight > current.clientHeight + 1
		) {
			fallback = current;
		}
		current = current.parentElement;
	}

	return fallback;
};

/** Align `element` to the top of its nearest scrollport, if scrolling is possible. */
const scrollElementToStartOfNearestScroller = (element: HTMLElement) => {
	const scroller = getNearestOverflowYScroller(element);
	if (!scroller) return;

	const delta =
		element.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
	if (Math.abs(delta) < 2) return;

	const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
	const nextScrollTop = Math.max(
		0,
		Math.min(scroller.scrollTop + delta, maxScroll),
	);
	if (Math.abs(nextScrollTop - scroller.scrollTop) < 2) return;

	scroller.scrollTo({ behavior: "smooth", top: nextScrollTop });
};

export const Accordion = (props: AccordionProps) => {
	const {
		buttonClassName,
		className,
		expandAllItemsByDefault,
		items,
		onOpenPanelsChange,
		openPanels: controlledOpenPanels,
		scrollOpenedPanelIntoView = false,
		transition = DEFAULT_ACCORDION_TRANSITION,
	} = props;

	if (import.meta.env.DEV && expandAllItemsByDefault && controlledOpenPanels) {
		console.warn(
			"Accordion: `expandAllItemsByDefault` is ignored when `openPanels` is provided (controlled mode).",
		);
	}

	const isControlled = controlledOpenPanels !== undefined;

	const [internalOpenPanels, setInternalOpenPanels] = useState(() => {
		if (isControlled) {
			return new Set<number>();
		}
		if (expandAllItemsByDefault) {
			return new Set(items.map((_, index) => index));
		}
		const defaultOpenIndices = items
			.map((item, index) => (item.defaultOpen ? index : -1))
			.filter((i): i is number => i >= 0);
		return new Set(defaultOpenIndices);
	});
	const openPanels = isControlled ? controlledOpenPanels : internalOpenPanels;
	// Parents pass a new Set on unrelated re-renders. Track the open indices so
	// those renders do not cancel the scroll.
	const openPanelsKey = [...openPanels].sort((a, b) => a - b).join(",");
	const openPanelsRef = useRef(openPanels);
	const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
	const needsScrollRef = useRef<Set<number>>(new Set());
	const prevOpenPanelsRef = useRef<Set<number>>(new Set());
	const prevOpenPanelsKeyRef = useRef<null | string>(null);
	const [openSnapshot, setOpenSnapshot] = useState<null | string>(null);
	const [settledPanels, setSettledPanels] = useState<Set<number>>(
		() => new Set(),
	);

	if (openSnapshot === null) {
		setOpenSnapshot(openPanelsKey);
		setSettledPanels(new Set(indicesFromKey(openPanelsKey)));
	} else if (openSnapshot !== openPanelsKey) {
		const previousIndices = new Set(indicesFromKey(openSnapshot));
		const nextIndices = new Set(indicesFromKey(openPanelsKey));
		setOpenSnapshot(openPanelsKey);
		setSettledPanels((current) => {
			const next = new Set(current);
			for (const index of nextIndices) {
				if (!previousIndices.has(index)) next.delete(index);
			}
			for (const index of previousIndices) {
				if (!nextIndices.has(index)) next.delete(index);
			}
			return next;
		});
	}

	useLayoutEffect(() => {
		openPanelsRef.current = openPanels;
		const current = new Set(indicesFromKey(openPanelsKey));

		if (prevOpenPanelsKeyRef.current === null) {
			prevOpenPanelsKeyRef.current = openPanelsKey;
			prevOpenPanelsRef.current = current;
			return;
		}

		if (prevOpenPanelsKeyRef.current === openPanelsKey) return;

		const previous = prevOpenPanelsRef.current;
		for (const index of current) {
			if (!previous.has(index)) needsScrollRef.current.add(index);
		}
		for (const index of previous) {
			if (!current.has(index)) needsScrollRef.current.delete(index);
		}
		prevOpenPanelsKeyRef.current = openPanelsKey;
		prevOpenPanelsRef.current = current;
	}, [openPanels, openPanelsKey]);

	const settleOpenedPanel = (index: number) => {
		if (!openPanelsRef.current.has(index)) return;

		setSettledPanels((current) => {
			if (current.has(index)) return current;
			const next = new Set(current);
			next.add(index);
			return next;
		});

		if (!scrollOpenedPanelIntoView || !needsScrollRef.current.has(index))
			return;
		needsScrollRef.current.delete(index);
		const itemElement = itemRefs.current[index];
		if (!itemElement) return;

		requestAnimationFrame(() => {
			if (!openPanelsRef.current.has(index)) return;
			scrollElementToStartOfNearestScroller(itemElement);
		});
	};

	const handleToggle = (index: number) => {
		const next = new Set(openPanels);
		if (next.has(index)) {
			next.delete(index);
		} else {
			next.add(index);
		}

		if (isControlled) {
			onOpenPanelsChange?.(next);
		} else {
			setInternalOpenPanels(next);
		}
	};

	const panelTransition = resolveAccordionTransition(transition);

	const renderToggleIcon = (index: number) => (
		<span
			aria-hidden="true"
			className="flex size-6 items-center text-fg-quaternary"
		>
			<svg
				fill="none"
				height="24"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="2"
				viewBox="0 0 24 24"
				width="24"
			>
				<circle cx="12" cy="12" r="10"></circle>
				<line
					className={cx(
						"origin-center rotate-0 transition duration-150 ease-out",
						openPanels.has(index) && "-rotate-90",
					)}
					x1="12"
					x2="12"
					y1="8"
					y2="16"
				></line>
				<line x1="8" x2="16" y1="12" y2="12"></line>
			</svg>
		</span>
	);

	return (
		<section className={cx("flex w-full min-w-0 flex-col gap-8", className)}>
			{items.map((item, index) => (
				<div
					className="w-full min-w-0 not-first:-mt-px not-first:border-t not-first:border-secondary"
					key={item.id ?? index}
					ref={(element) => {
						itemRefs.current[index] = element;
					}}
				>
					{typeof item.title === "string" ? (
						<h3>
							<button
								aria-expanded={openPanels.has(index)}
								className={cx(
									"flex w-full cursor-pointer items-start justify-between gap-2 rounded-md text-left outline-focus-ring select-none focus-visible:outline-2 focus-visible:outline-offset-2 md:gap-2",
									buttonClassName,
									item.headerClassName,
								)}
								onClick={() => handleToggle(index)}
								type="button"
							>
								<span className="w-full truncate text-md text-primary">
									<p className="text-md font-semibold text-primary">
										{item.title}
									</p>
								</span>
								{renderToggleIcon(index)}
							</button>
						</h3>
					) : (
						<h3
							className={cx(
								"flex w-full items-center justify-between gap-2",
								item.headerClassName,
							)}
						>
							<div className="min-w-0 flex-1">{item.title}</div>

							<button
								aria-expanded={openPanels.has(index)}
								aria-label={
									openPanels.has(index) ? "Collapse section" : "Expand section"
								}
								className={cx(
									"flex size-6 shrink-0 cursor-pointer items-center rounded-md text-fg-quaternary outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
									buttonClassName,
								)}
								onClick={() => handleToggle(index)}
								type="button"
							>
								{renderToggleIcon(index)}
							</button>
						</h3>
					)}

					<div
						onTransitionEnd={(event: TransitionEvent<HTMLDivElement>) => {
							if (event.target !== event.currentTarget) return;
							if (event.propertyName !== "grid-template-rows") return;
							settleOpenedPanel(index);
						}}
						style={{
							...panelTransition,
							display: "grid",
							gridTemplateColumns: "minmax(0, 1fr)",
							gridTemplateRows: openPanels.has(index) ? "1fr" : "0fr",
							minWidth: 0,
							opacity: openPanels.has(index) ? 1 : 0,
							overflow: settledPanels.has(index) ? "visible" : "hidden",
							transitionProperty: "grid-template-rows, opacity",
						}}
					>
						<div
							className={
								settledPanels.has(index)
									? "min-w-0"
									: "min-h-0 min-w-0 overflow-hidden"
							}
						>
							{typeof item.content === "string" ? (
								<p className="text-md text-tertiary">{item.content}</p>
							) : (
								item.content
							)}
						</div>
					</div>
				</div>
			))}
		</section>
	);
};
